// eslint:disable max-func-body-length
import { maxLengthOfEmailsInputValue, ReportTranslation } from 'app/area/company-report/constants';
import { Metadata, ScheduleDefinition, ScheduleFailedConstraints } from 'app/area/company-report/types-store';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { FiltersCompanyReport } from 'app/area/filters/filters-company-report';
import { selectCurrentList, selectReportData } from 'app/area/filters/selectors';
import { CreateReport } from 'app/area/saved-reports/create-report';
import { SpinLoader } from 'app/components';
import { ComplexValue, FavouriteDefinition, SharedState } from 'app/types-shared';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ButtonCallToAction, MeatballMenu, Textarea, ValidationLevel, ValidationProps } from 'ui';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { ScheduleReport } from 'app/area/company-report/components/schedule-report';
import styles from './report-screen.scss';
import { useEmailSubscribersToCompanyReportDefinitionEffect} from 'app/area/company-report/hooks';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { Tag } from 'app/components/tag';
import { Icon } from 'ui/common/icon';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { routeConstants } from 'app/routes/route-constants';
import { Link } from 'react-router-dom';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';
import { replaceTemplateString } from 'app/infrastructure/platform/text';
import { FaUser } from 'node_modules/react-icons/fa';

interface ReportScreenProps {
  companyReportId: number;
  sharedState: SharedState;
  translation: any;
  onChangeEmail: (value: string, validation?: ValidationProps) => void;
  keyMap: KeyMap;
  handlers: Handlers;
  sendReport: () => void;
  saveReport: () => void;
  emails: string;
  metadata: Metadata;
  fetchingReport: boolean;
  fetchingMetadata: boolean;
  emailsValidation?: ValidationProps;
  validateFilters: boolean;

  isCreateReportOpen: boolean;
  onCloseReport: () => void;
  onCreateReport: () => void;
  onUpdateReport: () => void;
  searchRequiredUsers: boolean;
  updateExistingReport: boolean;
  definition: FavouriteDefinition;
  deleteScheduleDefinition: () => void;
  showUpdate?: boolean;
  reportIsDisabled?: boolean;
  scheduleDefinition: ScheduleDefinition;
  updateScheduleDefinition: (scheduleDefinition: ScheduleDefinition, validateSchedule?: boolean) => void;
  fetchingScheduleDefinition: boolean;
  scheduleDefinitionChanged: boolean;
  createScheduleDefinition: () => void;
  scheduleFailedConstraints: ScheduleFailedConstraints;
  updateScheduleDefinitionOnSubmit: () => void;
  initialOwners?: ComplexValue<number>[];
}

interface EmailRecipientsProps {
  emails: string;
  fetchingReport: boolean;
  handlers: Handlers;
  keyMap: KeyMap;
  onChangeEmail: (value: string, validation?: ValidationProps) => void;
  translation: any;
}

function EmailRecipients({ emails, fetchingReport, handlers, keyMap, onChangeEmail, translation }: EmailRecipientsProps) {
  const [invalidEmail, setInvalidEmail] = React.useState(false);
  const errorParamsRequired = {
    level: ValidationLevel.error,
    messages: [{ level: ValidationLevel.error, message: `${translation.invalid_email_error}` }]
  };
  const emailValidation = !!emails && invalidEmail && !isValidListOfEmails(emails) && errorParamsRequired;

  const onBlurEmail = () => {
    const hasValidEmails = isValidListOfEmails(emails);
    setInvalidEmail(!hasValidEmails);
    return hasValidEmails;
  };

  return <>
    <div>
      <h3 className="tertiary-group-title">
        <span>{translation.generate_title}</span>
      </h3>
    </div>
    <HotKeys keyMap={keyMap} handlers={handlers}>
      <div>
        <div className="row">
          <div className="col-12 col-md-7">
            <Textarea
              componentId="0"
              className="email-input"
              onChange={onChangeEmail}
              readOnly={fetchingReport}
              validation={emailValidation}
              value={emails}
              onBlur={onBlurEmail}
              maxLength={maxLengthOfEmailsInputValue}
              placeholder={translation.enter_email_recipients}
            />
          </div>
        </div>
      </div>
    </HotKeys>
  </>;
}

interface ReportActionsMenuProps {
  companyReportId: number;
  companyReportAdmin: boolean;
  onCreateReport: () => void;
  onUpdateReport: () => void;
  reportIsDisabled?: boolean;
  showUpdate?: boolean;
  translation: any;
}

interface UpdateReportActionProps {
  onUpdateReport: () => void;
  reportIsDisabled?: boolean;
  showUpdate?: boolean;
  translation: any;
}

function UpdateReportAction({ onUpdateReport, reportIsDisabled, showUpdate, translation }: UpdateReportActionProps) {
  return showUpdate ? <li>
    <button type="button" onClick={onUpdateReport} className="dropdown-item" disabled={reportIsDisabled}>
      {translation.update_existing_company_report}
    </button>
  </li> : null;
}

interface AdminReportActionsProps {
  companyReportAdmin: boolean;
  companyReportId: number;
  translation: any;
}

function AdminReportActions({ companyReportAdmin, companyReportId, translation }: AdminReportActionsProps) {
  return companyReportAdmin ? <>
    <li>
      <Link className="dropdown-item" to={routeConstants.companyReportAdminItemFor(companyReportId)}>
        {translation.edit_company_report_config}
      </Link>
    </li>
    <li>
      <Link className="dropdown-item" to={routeConstants.companyReportAdminList}>
        {translation.manage_company_reports_option}
      </Link>
    </li>
  </> : null;
}

function ReportActionsMenu({
  companyReportId, companyReportAdmin, onCreateReport, onUpdateReport, reportIsDisabled, showUpdate, translation
}: ReportActionsMenuProps) {
  return <div className="meatball-menu float-end position-relative">
    <MeatballMenu closeAfterClick={true}>
      <li>
        <button type="button" onClick={onCreateReport} className="dropdown-item" disabled={reportIsDisabled}>
          {translation.save_new_company_report}
        </button>
      </li>
      <UpdateReportAction
        onUpdateReport={onUpdateReport}
        reportIsDisabled={reportIsDisabled}
        showUpdate={showUpdate}
        translation={translation}
      />
      <AdminReportActions
        companyReportAdmin={companyReportAdmin}
        companyReportId={companyReportId}
        translation={translation}
      />
    </MeatballMenu>
  </div>;
}

interface ScheduleStatusProps {
  scheduleDefinition: ScheduleDefinition;
  translation: ReportTranslation;
}

function ScheduleStatus({ scheduleDefinition, translation }: ScheduleStatusProps) {
  return <>
    <div>
      <h3 className="tertiary-group-title">
        <span>{translation.schedule_title}</span>
      </h3>
      <span className={styles.iconSchedule}><Icon url={toAbsoluteUrl('i/recurrence.svg')} /></span>
      <span>
        {!!scheduleDefinition ? translation.scheduled_for_auto_execute : translation.not_scheduled_for_auto_execute}
      </span>
    </div>
    {scheduleDefinition?.scheduleOwner?.description && <div>
      <span className={styles.iconScheduleOwner}><FaUser /></span>
      <span>
        {replaceTemplateString(translation.schedule_owned_by, scheduleDefinition?.scheduleOwner?.description)}
      </span>
    </div>}
  </>;
}

interface GenerateReportButtonProps {
  currentListFilters: unknown[];
  emails: string;
  fetchingMetadata: boolean;
  isCreateReportOpen: boolean;
  metadata: Metadata;
  saveReport: () => void;
  sendReport: () => void;
  translation: any;
}

function GenerateReportButton({
  currentListFilters, emails, fetchingMetadata, isCreateReportOpen, metadata, saveReport, sendReport, translation
}: GenerateReportButtonProps) {
  if (!metadata) {
    return null;
  }

  if (fetchingMetadata) {
    return null;
  }

  if (isCreateReportOpen) {
    return null;
  }

  const onGenerateReport = metadata.eMailReport && emails ? sendReport : saveReport;

  return <div className={styles.generateButton}>
    <ButtonCallToAction onClick={onGenerateReport} disabled={!currentListFilters.length}>
      {translation.generate_button}
    </ButtonCallToAction>
  </div>;
}

interface ReportHeadingProps {
  definition: FavouriteDefinition;
  reportIsDisabled?: boolean;
  reportTypeDescription?: string;
  translation: any;
}

function ReportHeading({ definition, reportIsDisabled, reportTypeDescription, translation }: ReportHeadingProps) {
  return <>
    <h1 className={styles.header}>{reportTypeDescription}
      {reportIsDisabled && <span className={styles.disabledTag}>
        <Tag>{translation.disabled}</Tag>
      </span>}
    </h1>
    <h3>{definition?.favourite?.d || translation.report_title}</h3>
  </>;
}

function ReportLoadingIndicator({ fetchingMetadata, fetchingReport }: Pick<ReportScreenProps, 'fetchingMetadata' | 'fetchingReport'>) {
  return fetchingReport || fetchingMetadata ? <SpinLoader /> : null;
}

function ScheduleSection({ isCreateReportOpen, scheduleDefinition, translation }: Pick<ReportScreenProps,
  'isCreateReportOpen' | 'scheduleDefinition' | 'translation'>) {
  return isCreateReportOpen ? null : <ScheduleStatus scheduleDefinition={scheduleDefinition} translation={translation} />;
}

interface EmailRecipientsSectionProps extends EmailRecipientsProps {
  hasEmailReport: boolean;
}

function EmailRecipientsSection({ hasEmailReport, ...emailRecipientsProps }: EmailRecipientsSectionProps) {
  return hasEmailReport ? <EmailRecipients {...emailRecipientsProps} /> : null;
}

export function ReportScreen({
  metadata, translation, sharedState, onChangeEmail, fetchingReport, fetchingScheduleDefinition, keyMap, handlers, 
  emails, sendReport, saveReport, companyReportId, validateFilters, fetchingMetadata, isCreateReportOpen, searchRequiredUsers, 
  updateExistingReport, onCloseReport, definition, onCreateReport, onUpdateReport, showUpdate, reportIsDisabled, deleteScheduleDefinition, 
  scheduleDefinition, updateScheduleDefinition, createScheduleDefinition, scheduleFailedConstraints, updateScheduleDefinitionOnSubmit,
  initialOwners = []
}: ReportScreenProps) {

  useEmailSubscribersToCompanyReportDefinitionEffect(emails, metadata?.eMailReport);
  const instanceKey = useInstanceKey();
  const reportData = useSelector(selectReportData(instanceKey));
  const currentListFilters = useSelector(selectCurrentList(instanceKey));
  const companyReportAdmin = sharedState.access?.adminPanel?.companyReport?.administrationCentral ?? false;

  return (
    <MainGridContainer className={styles.reportPanel}>
      <ReportActionsMenu
        companyReportId={companyReportId}
        companyReportAdmin={companyReportAdmin}
        onCreateReport={onCreateReport}
        onUpdateReport={onUpdateReport}
        reportIsDisabled={reportIsDisabled}
        showUpdate={showUpdate}
        translation={translation}
      />
      <GenerateReportButton
        currentListFilters={currentListFilters}
        emails={emails}
        fetchingMetadata={fetchingMetadata}
        isCreateReportOpen={isCreateReportOpen}
        metadata={metadata}
        saveReport={saveReport}
        sendReport={sendReport}
        translation={translation}
      />
      <ReportHeading
        definition={definition}
        reportIsDisabled={reportIsDisabled}
        reportTypeDescription={reportData?.reportTypeDescription}
        translation={translation}
      />
      {isCreateReportOpen && <CreateReport
        closeReportCreator={onCloseReport}
        definition={definition}
        searchRequiredUsers={searchRequiredUsers}
        updateReport={updateExistingReport}
        companyReportId={companyReportId}
        className={styles.createReportSection}
        update_report_title={translation.update_existing_company_report}
        title={translation.save_new_company_report}
        reportIsDisabled={reportIsDisabled}
        scheduleFailedConstraints={scheduleFailedConstraints}
        updateScheduleDefinitionOnSubmit={updateScheduleDefinitionOnSubmit}
        hasScheduleDefinition={!!scheduleDefinition}
      >
        <ScheduleReport
          definition={definition}
          onUpdateReport={onUpdateReport}
          showUpdate={showUpdate}
          deleteScheduleDefinition={deleteScheduleDefinition}
          translation={translation}
          initialOwners={initialOwners}
          scheduleDefinition={scheduleDefinition}
          updateScheduleDefinition={updateScheduleDefinition}
          fetchingScheduleDefinition={fetchingScheduleDefinition}
          locale={sharedState?.locale}
          reportIsDisabled={reportIsDisabled}
          createScheduleDefinition={createScheduleDefinition}
          scheduleFailedConstraints={scheduleFailedConstraints}
        />
      </CreateReport>}
      <FiltersCompanyReport
        companyReportId={companyReportId}
        definition={definition}
        subtitle={translation.report_subtitle}
        predefinedTitle={translation.filters_predefined_date}
        validateFilters={validateFilters}
        currentList={currentListFilters}
        locale={sharedState?.locale}
      />
      <ScheduleSection isCreateReportOpen={isCreateReportOpen} scheduleDefinition={scheduleDefinition} translation={translation} />
      <EmailRecipientsSection hasEmailReport={!!metadata?.eMailReport}
        emails={emails}
        fetchingReport={fetchingReport}
        handlers={handlers}
        keyMap={keyMap}
        onChangeEmail={onChangeEmail}
        translation={translation}
      />
      <ReportLoadingIndicator fetchingMetadata={fetchingMetadata} fetchingReport={fetchingReport} />
    </MainGridContainer>
  );
}
